import { remove as removeDiacritics } from 'diacritics';
import { UAParser } from 'ua-parser-js';

import settings from '/@/core/settings';
import { _, getActiveLanguage } from '/@/locale';
import type { Geometry, MultiPolygon, OldPolygon, Point } from '../core/fields/geometry';
import type { User } from '../types/core';

export const isMobile = () => {
    let size = false;
    try {
        size = window.innerWidth <= 900;
    } catch (error) {}
    return ['mobile', 'tablet'].includes(new UAParser().getDevice().type ?? '') || size;
};
export const isTablet = () => {
    let size = false;
    try {
        size = window.innerWidth <= 900;
    } catch (error) {}
    return ['tablet'].includes(new UAParser().getDevice().type ?? '') || size;
};
export const isPhone = () => {
    let size = false;
    try {
        size = window.innerWidth <= 820;
    } catch (error) {}
    return ['mobile'].includes(new UAParser().getDevice().type ?? '') || size;
};

export const getOS = () => new UAParser().getOS().name ?? undefined;
export const getOSVersion = () => new UAParser().getOS().version ?? undefined;
export const getBrowser = () => new UAParser().getBrowser().name ?? undefined;
export const getBrowserVersion = () => new UAParser().getBrowser().version ?? undefined;

export const isStandAlone = () => {
    switch (getOS()) {
        case 'Android':
            return window.matchMedia('(display-mode: standalone)').matches;

        case 'iOS':
            return window.navigator.standalone;

        default:
            if (Object.hasOwn(window.navigator, 'standalone')) {
                return window.navigator.standalone;
            }
            return window.matchMedia('(display-mode: standalone)').matches;
    }
};

export const sum = (list) => list.reduce((n, t) => n + t, 0);
export const range = (length) => [...Array(length).keys()];
export const flatMap = (list, key) => {
    const result = new Set();
    list.forEach((item) => {
        const value = typeof key === 'function' ? key(item) : item[key];
        if (value) {
            if (Array.isArray(value)) return value.forEach(result.add.bind(result));
            return result.add(value);
        }
    });
    return [...result];
};

export const validateLength = (value, min = 0) => {
    return String(value).length > min || _('Por favor ingresá más de {{ min }} caractéres.', { min });
};

export const validateMatch = (a, b) => {
    return a === b || _('Los valores no coinciden.');
};

export const validatePassword = (value, reference) => {
    const lengthValidation = validateLength(value, 4);
    if (lengthValidation !== true) return lengthValidation;
    const matchValidation = validateMatch(value, reference);
    if (matchValidation !== true) return matchValidation;
    return true;
};

/** Match Identity / Django: canonical email for auth and profile API calls. */
export const normalizeAuthEmail = (email: string | null | undefined): string =>
    String(email ?? '')
        .trim()
        .toLowerCase();

export const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email) ? true : _('Por favor ingresá un email válido.');
};

export const validateEmailV2 = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
};

export const toTitleCase = (value) => value?.[0]?.toUpperCase() + value.substr(1);
export const toFixedNumber = (number, places = 1) => (number ? Number(number.toFixed(places)) : number);

export const localeSort =
    (key, direction = 1) =>
    (a, b) => {
        if (typeof a[key] === 'string') {
            return a?.[key]?.localeCompare(b[key]) * direction;
        }

        return _(a[key]).localeCompare(_(b[key])) * direction;
    };
export const sanitize = (str) => removeDiacritics(String(str)).trim().toLowerCase();

export const slugify = (str) =>
    sanitize(str).replace(/[()]/g, '').replace(/-/g, ' ').replace(/\s+/g, ' ').replace(/\s/g, '-');

export const getUrlPath = (parts) =>
    parts.reduce((url, str) => {
        return url + '/' + slugify(str);
    }, '');

export const parts = (str) =>
    sanitize(str)
        .split(' ')
        .map((str) => str.trim())
        .filter(Boolean);

export const findOption = (options, value) => options.find((option) => option.value === value);
export const findLabel = (options, value, fallback) => {
    const option = findOption(options, value);
    return option ? option.label : fallback;
};

export const findAndClone = (items, fn) => {
    const item = items.find(fn);
    return item ? { ...item } : undefined;
};

export const findAndCloneById = (items, id) => findAndClone(items, (item) => item.id === id);

export const checkRange = (value, minKey, maxKey) => (item) =>
    (!item[minKey] || item[minKey] <= value) && (!item[maxKey] || value < item[maxKey]);

export const getGroups = (items, count) => {
    const groups = [];
    const length = Number(Math.ceil(items.length / count));
    let list = [...items];
    while (list.length > length) {
        groups.push(list.slice(0, length));
        list = list.slice(length);
    }
    groups.push(list);
    return groups;
};

type Entries<T> = {
    [K in keyof T]: [K, T[K]];
}[keyof T][];
export const objectEntries = <T extends object>(obj: T): Entries<T> => {
    return Object.entries(obj) as Entries<T>;
};

type Grouper<T, K> = ((item: T) => K) | keyof T;
export const groupBy = <T, K>(collection: T[], grouper: Grouper<T, K>): Record<K, T[]> =>
    collection.reduce(
        (grouped, item) => {
            const group = typeof grouper === 'function' ? grouper(item) : item[grouper];
            (grouped[group] = grouped[group] || []).push(item);
            return grouped;
        },
        {} as Record<K, T[]>
    );

export function pickKeys<T, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
    const result = {} as Pick<T, K>;
    keys.forEach((key) => {
        if (key in obj) {
            result[key] = obj[key];
        }
    });
    return result;
}

export const excludeKeys = (object, keys = []) =>
    Object.entries(object).reduce((hash, [key, value]) => {
        if (!keys.includes(key)) hash[key] = value;
        return hash;
    }, {});

export const buildOptions = (items, getters = {}) => {
    const getterMap = {
        value: (item) => item.id,
        label: (item) => _(item.name),
        ...getters,
    };

    return items.map((item) => ({
        value: getterMap.value(item),
        label: getterMap.label(item),
    }));
};

export const getMonthName = (monthIndex) => {
    if (monthIndex < 1 || monthIndex > 12) throw new Error('OutOfRange');
    return settings.enums.months?.[monthIndex]?.label;
};

export const formatEnvironmentRange = ({ min, max }, toYield) => {
    if (min && max) return `${toYield(min)} ~ ${toYield(max)}`;
    if (min) return `> ${toYield(min)}`;
    if (max) return `< ${toYield(max)}`;
    return _('Todos');
};

export const toDegreesMinutesAndSeconds = (coordinate) => {
    const degrees = Math.floor(coordinate);
    const minutesNotTruncated = (coordinate - degrees) * 60;
    const minutes = Math.floor(minutesNotTruncated);
    const seconds = Math.floor((minutesNotTruncated - minutes) * 60);
    const rest = minutesNotTruncated - minutes;

    return degrees + '° ' + minutes + "' " + seconds + '"';
};

const getSimplifiedIcon = (marker) => {
    const { externalProvider, markerType } = marker;
    if (markerType === 'trial') return 'trial';
    if (externalProvider === 'fieldview') return 'fieldview';
    return 'point';
};

export const getMarkerIcon = (marker, simplifyPolygons) => {
    return getSimplifiedIcon(marker);
};

export const getAllDays = () =>
    ({
        Mon: _('Lun'),
        Tue: _('Mar'),
        Wed: _('Mie'),
        Thu: _('Jue'),
        Fri: _('Vie'),
        Sat: _('Sab'),
        Sun: _('Dom'),
    }) as const;

export const getAllMonths = () =>
    ({
        Jan: _('Enero'),
        Feb: _('Febrero'),
        Mar: _('Marzo'),
        Apr: _('Abril'),
        May: _('Mayo'),
        Jun: _('Junio'),
        Jul: _('Julio'),
        Aug: _('Agosto'),
        Sep: _('Septiembre'),
        Oct: _('Octubre'),
        Nov: _('Noviembre'),
        Dec: _('Diciembre'),
    }) as const;

export const getCampaignString = (year) => {
    return `${year}/${year + 1}`;
};

export const replaceSpecialCharacters = (value) => {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

// My Recommendations
export const translateType = (value) => {
    return value === 'corn' ? _('Maíz') : _('Soja');
};

export const generateOptionsKeyValue = (value, recommendations) => {
    let unique = [];
    if (value === 'product') {
        unique = [
            ...recommendations
                .map((prods) => prods[value])
                .reduce((acc, prods) => {
                    prods.forEach((prod) => acc.add(prod));
                    return acc;
                }, new Set()),
        ];
    } else {
        unique = [...new Set(recommendations.map((item) => item[value]))];
    }
    const uniqueValues = unique
        .map((uni, index) => ({
            index,
            value: uni,
            label: uni === '' ? _('Vacío') : value === 'type' ? translateType(uni) : uni,
        }))
        .sort((a, b) => {
            if (value === 'created') return new Date(a.value) - new Date(b.value);
            if (value === 'plantingDate') return new Date(a.value) - new Date(b.value);
            if (value === 'environment')
                return Number.parseInt(a.value.split(' ')[0]) - Number.parseInt(b.value.split(' ')[0]);
            return a.value.localeCompare(b.value);
        });
    return uniqueValues;
};

export const timeSince = (date, language) => {
    const seconds = Math.floor((new Date() - date) / 1000);

    let interval = seconds / 31536000;

    if (interval > 1) {
        if (language === 'es') {
            return `Hace ${Math.floor(interval)} ${Math.floor(interval) === 1 ? 'año' : 'años'}`;
        }
        return `${Math.floor(interval)} ${Math.floor(interval) === 1 ? _('año') : _('años')} ${_('atrás')}`;
    }
    interval = seconds / 2592000;
    if (interval > 1) {
        if (language === 'es') {
            return `Hace ${Math.floor(interval)} ${Math.floor(interval) === 1 ? 'mes' : 'meses'}`;
        }
        return `${Math.floor(interval)} ${Math.floor(interval) === 1 ? _('mes') : _('meses')} ${_('atrás')}`;
    }
    interval = seconds / 86400;
    if (interval > 1) {
        if (language === 'es') {
            return `Hace ${Math.floor(interval)} ${Math.floor(interval) === 1 ? 'día' : 'días'}`;
        }
        return `${Math.floor(interval)} ${Math.floor(interval) === 1 ? _('día') : _('días')} ${_('atrás')}`;
    }
    interval = seconds / 3600;
    if (interval > 1) {
        if (language === 'es') {
            return `Hace ${Math.floor(interval)} ${Math.floor(interval) === 1 ? 'hora' : 'horas'}`;
        }
        return `${Math.floor(interval)} ${Math.floor(interval) === 1 ? _('hora') : _('horas')} ${_('atrás')}`;
    }
    interval = seconds / 60;
    if (interval > 1) {
        if (language === 'es') {
            return `Hace ${Math.floor(interval)} ${Math.floor(interval) === 1 ? 'minuto' : 'minutos'}`;
        }
        return `${Math.floor(interval)} ${Math.floor(interval) === 1 ? _('minuto') : _('minutos')} ${_('atrás')}`;
    }

    return _('Hace unos instantes');
};

type FormatDateCMOptions = {
    dayMonthOnly?: boolean;
    format?: 'short' | 'long' | 'full';
};

export const formatDate = (dateApi: Date, options?: FormatDateCMOptions) => {
    const language = getActiveLanguage();
    const languages = {
        es: 'es-AR',
        pt: 'pt-BR',
        en: 'en-US',
    };

    // Full format: "Monday, January 15, 2024"
    if (options?.format === 'full') {
        return dateApi.toLocaleDateString(languages[language], {
            timeZone: 'UTC',
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    }

    // Long format: "15 January" or "January 15"
    if (options?.format === 'long') {
        return dateApi.toLocaleDateString(languages[language], {
            timeZone: 'UTC',
            month: 'long',
            day: 'numeric',
        });
    }

    // Short format: "15 Jan" or "Jan 15"
    if (options?.format === 'short') {
        return dateApi.toLocaleDateString(languages[language], {
            timeZone: 'UTC',
            month: 'short',
            day: 'numeric',
        });
    }

    // Day and month only: "01/15" or "15/01"
    if (options?.dayMonthOnly) {
        return dateApi.toLocaleDateString(language, {
            timeZone: 'UTC',
            month: '2-digit',
            day: '2-digit',
        });
    }

    // Default: Full date "01/15/2024" or "15/01/2024"
    return dateApi.toLocaleDateString(language, {
        timeZone: 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
};

export const formatDateCMProgressBar = (fullDate) => {
    const activeLanguage = getActiveLanguage();
    let [day, month] = fullDate.split('/');
    if (activeLanguage === 'en') {
        [day, month] = [month, day];
    }
    const monthClear = Number.parseInt(month.replace(/^0+/, ''));

    const allMonths = {
        1: _('ENE'),
        2: _('FEB'),
        3: _('MAR'),
        4: _('ABR'),
        5: _('MAY'),
        6: _('JUN'),
        7: _('JUL'),
        8: _('AGO'),
        9: _('SEP'),
        10: _('OCT'),
        11: _('NOV'),
        12: _('DIC'),
    };

    return `${day} ${allMonths[monthClear]}`;
};

export function addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

export const getLanguage = (language: string) => {
    if (language === 'es') return 'es-ES';
    if (language === 'en') return 'en-US';
    if (language === 'pt') return 'pt-br';
};

export const toLocaleDateString = (date, options = { month: 'long', day: 'numeric' }) => {
    const language = getActiveLanguage();
    const languages = {
        es: 'es-AR',
        pt: 'pt-BR',
        en: 'en-US',
    };

    // Ensure timezone is UTC if not explicitly provided
    const dateOptions = { timeZone: 'UTC', ...options };
    return date.toLocaleDateString(languages[language], dateOptions);
};

export const capitalizeFirstLetter = (word: string) => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

export const convertSnakeToCamelCase = (text) => {
    return text.replace(/([-_]\w)/g, (g) => g?.[1]?.toUpperCase());
};

export const findClosestDensity = (targetEnv, densityList) => {
    densityList.sort((a, b) => a.estimatedYield - b.estimatedYield);
    const getAbsoluteDifference = (a, b) => Math.abs(a - b);
    let closestDensity = densityList[0];
    let closestDifference = getAbsoluteDifference(targetEnv, closestDensity.estimatedYield);
    for (let i = 1; i < densityList.length; i++) {
        const currentDensity = densityList[i];
        const currentDifference = getAbsoluteDifference(targetEnv, currentDensity.estimatedYield);
        if (currentDifference < closestDifference) {
            closestDensity = currentDensity;
            closestDifference = currentDifference;
        }
    }

    return closestDensity;
};

export const isUserValidatedType = (user: User, types: string[]) => {
    if (!user) return false;
    return user.type === 'bayer' && types.includes(user.subtype) && user.validated_type;
};

export const isUserValidatedRV = (user: User) => {
    const RV_USER_SUBTYPES = ['sales'];
    return isUserValidatedType(user, RV_USER_SUBTYPES);
};

export const isUserValidatedSquad = (user: User) => {
    const SQUAD_USER_SUBTYPES = ['squad_coach'];
    return isUserValidatedType(user, SQUAD_USER_SUBTYPES);
};

export const isUserValidatedConsultant = (user: User) => {
    const CONSULTANT_USER_SUBTYPES = ['consultant'];
    return isUserValidatedType(user, CONSULTANT_USER_SUBTYPES);
};

export const isUserValidatedAdvisor = (user: User) => {
    const ADVISOR_USER_SUBTYPES = ['advisor'];
    return isUserValidatedType(user, ADVISOR_USER_SUBTYPES);
};

export const isValidatedBayerUserType = (user: User) => {
    return (
        isUserValidatedConsultant(user) ||
        isUserValidatedRV(user) ||
        isUserValidatedSquad(user) ||
        isUserValidatedAdvisor(user)
    );
};

export const openWhatsappWeb = ({ message, phone }: { message?: string; phone?: string }) => {
    const url = 'https://wa.me/';
    const urlSearchParams = new URLSearchParams();
    if (message) {
        urlSearchParams.set('text', message);
    }
    if (phone) {
        urlSearchParams.set('phone', phone);
    }
    window.open(`${url}?${urlSearchParams.toString()}`, '_blank');
};

export const sliceString = (string: string, length: number) => {
    return `${string.slice(0, length)}${string.length > length ? '...' : ''}`;
};

/**
 * Type guard: Check if geometry is a Point
 * Point format: [[lng, lat]]
 */
export const isPoint = (geometry: Geometry | null | undefined): geometry is Point => {
    if (!geometry || !Array.isArray(geometry)) return false;
    if (geometry.length !== 1) return false;
    if (!Array.isArray(geometry[0])) return false;
    return geometry[0].length === 2 && typeof geometry[0][0] === 'number' && typeof geometry[0][1] === 'number';
};

/**
 * Type guard: Check if geometry is an OldPolygon (legacy format)
 * OldPolygon format: [[lng, lat], [lng, lat], ...] (flat array, length > 1)
 */
export const isOldPolygon = (geometry: Geometry | OldPolygon | null | undefined): geometry is OldPolygon => {
    if (!geometry || !Array.isArray(geometry)) return false;
    if (geometry.length <= 1) return false; // Distinguish from Point
    if (!Array.isArray(geometry[0])) return false;
    return geometry[0].length === 2 && typeof geometry[0][0] === 'number' && typeof geometry[0][1] === 'number';
};

/**
 * Type guard: Check if geometry is a MultiPolygon
 * MultiPolygon format: [[[[lng, lat], ...], ...], [[[lng, lat], ...], ...], ...]
 * Requires at least 3 levels of nesting
 */
export const isMultiPolygon = (geometry: Geometry | null | undefined): geometry is MultiPolygon => {
    if (!geometry || !Array.isArray(geometry) || geometry.length === 0) return false;
    if (!Array.isArray(geometry[0])) return false;
    if (geometry[0].length === 0) return false;
    if (!Array.isArray(geometry[0][0])) return false;
    if (geometry[0][0].length === 0) return false;
    // Check if third level contains coordinates (arrays of 2 numbers)
    return (
        Array.isArray(geometry[0][0][0]) &&
        geometry[0][0][0].length === 2 &&
        typeof geometry[0][0][0][0] === 'number' &&
        typeof geometry[0][0][0][1] === 'number'
    );
};

export const getInitials = (name: string): string =>
    name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0] ?? '')
        .join('')
        .toUpperCase();

export const getUserTypes = () => ({
    grower: _('Productor'),
    rv: _('RV'),
    consultant: _('Consultor'),
    squad_coach: _('Squad Coach'),
    advisor: _('Asesor'),
    other: _('Otro'),
});

export const getPluralUserTypes = () => ({
    grower: _('Productores'),
    rv: _('RVs'),
    consultant: _('Consultores'),
    squad_coach: _('Squad Coaches'),
    advisor: _('Asesores'),
    other: _('Otros'),
});
